import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useId, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import { useBodyScrollLock } from '../../../lib/useBodyScrollLock';
import type { OfferRequestSortKey } from '../types';
import { OfferRequestsToolbar } from './OfferRequestsToolbar';
import { PageHeading } from './PageHeading';

const FILTERS_COLUMN_WIDTH = 320;
const COLUMN_GAP = 24;

// Below this the filter column becomes a drawer behind a button in the page heading.
const DESKTOP = 'md';

export type OfferRequestsPageLayoutProps = {
    title: string;
    filters: ReactNode;
    status: 'pending' | 'error' | 'success';
    count: number;
    sort: OfferRequestSortKey;
    onSortChange: (sort: OfferRequestSortKey) => void;
    children: ReactNode;
    range?: { from: number; to: number };
    footer?: ReactNode;
};

export function OfferRequestsPageLayout({
    title,
    filters,
    status,
    count,
    sort,
    onSortChange,
    children,
    range,
    footer,
}: OfferRequestsPageLayoutProps): ReactElement {
    const { t } = useTranslation();
    const theme = useTheme();
    const headingId = useId();
    const compact = useMediaQuery(theme.breakpoints.down(DESKTOP));
    const [filtersOpen, setFiltersOpen] = useState(false);

    useBodyScrollLock(compact && filtersOpen);

    const filtersHeading = (
        <Typography variant="subtitle1" component="h2" id={headingId}>
            {t('OFFER_REQUESTS.FILTERS.TITLE')}
        </Typography>
    );

    return (
        <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', px: { xs: 2, [DESKTOP]: 3 } }}>
            <PageHeading
                title={title}
                actions={
                    compact ? (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FilterListIcon />}
                            aria-expanded={filtersOpen}
                            onClick={() => setFiltersOpen(true)}>
                            {t('OFFER_REQUESTS.FILTERS.TITLE')}
                        </Button>
                    ) : undefined
                }
            />

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        [DESKTOP]: `${FILTERS_COLUMN_WIDTH}px minmax(0, 1fr)`,
                    },
                    gap: `${COLUMN_GAP}px`,
                    alignItems: 'start',
                }}>
                {compact ? (
                    <Drawer
                        anchor="left"
                        open={filtersOpen}
                        onClose={() => setFiltersOpen(false)}
                        aria-label={t('OFFER_REQUESTS.FILTERS.SECTION')}
                        slotProps={{
                            paper: {
                                sx: {
                                    width: FILTERS_COLUMN_WIDTH,
                                    height: '100dvh',
                                    overflowY: 'auto',
                                    overscrollBehavior: 'contain',
                                },
                            },
                        }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                                pl: 2,
                                pr: 1,
                                py: 1,
                                position: 'sticky',
                                top: 0,
                                // Above MUI's outlined `InputLabel`, which sits at z-index 1 — at the same
                                // level the labels below win on DOM order and print through the header.
                                zIndex: 2,
                                bgcolor: 'background.paper',
                                borderBottom: 1,
                                borderColor: 'divider',
                            }}>
                            {filtersHeading}
                            <IconButton
                                onClick={() => setFiltersOpen(false)}
                                aria-label={t('OFFER_REQUESTS.FILTERS.CLOSE')}>
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                        <Box sx={{ p: 2 }}>{filters}</Box>
                    </Drawer>
                ) : (
                    <Box component="aside" aria-labelledby={headingId}>
                        <Box sx={{ px: 2, py: 1.5 }}>{filtersHeading}</Box>
                        {filters}
                    </Box>
                )}

                <Stack spacing={2} component="section" aria-label={t('OFFER_REQUESTS.RESULTS_SECTION')}>
                    <OfferRequestsToolbar
                        count={status === 'success' ? count : undefined}
                        sort={sort}
                        onSortChange={onSortChange}
                        range={status === 'success' ? range : undefined}
                    />

                    {match(status)
                        .with('pending', () => (
                            <Stack alignItems="center" sx={{ py: 6 }}>
                                <CircularProgress aria-label={t('OFFER_REQUESTS.LOADING')} />
                            </Stack>
                        ))
                        .with('error', () => <Alert severity="error">{t('OFFER_REQUESTS.ERROR.LOAD')}</Alert>)
                        .with('success', () => children)
                        .exhaustive()}

                    {status === 'success' && footer}
                </Stack>
            </Box>
        </Box>
    );
}
